import React, { useState, useEffect, useMemo } from 'react';
import { GiHealthNormal } from 'react-icons/gi';
import { Modal, Header, Button, Form, Loader, Dimmer } from 'semantic-ui-react';

import {
  ANSWER_IDS,
  FOOD_ORDER_TRIAGE_ASSIGN_TO,
  QUESTIONS,
  TRIAGE_QUESTION_ID,
  TRIAGE_SURVEY_QUESTION_ORDER,
} from './fixtures';
import { useFoodOrderTriageSurveyV2 } from './hooks';
import {
  WakeCountySection,
  MemberReceivedSection,
  ReceivedPathSection,
  NotReceivedPathSection,
  LinkOrderSection,
} from './sections';
import type { QuestionResponse } from './types';

interface FoodOrderTriageModalV2Props {
  deliveryDate?: string;
  foodProvider?: string;
  onClose: () => void;
  open: boolean;
  orderId?: string;
  ordersMutate: (data?: unknown, shouldRevalidate?: boolean) => Promise<unknown>;
  orgId: string;
  patientId: string;
  /** Raw orders data for building past orders list. Filtering is done in the modal. */
  ordersData?: Array<{
    orderId?: string;
    patientId?: string;
    patientOrg?: string;
    vendorId?: string;
    orderDate?: string;
    deliveryDate?: string;
  }>;
  /** Patient lookup for member names. Keyed by patientId. */
  patients?: Record<string, { firstName?: string; lastName?: string }>;
}

const DELIVERED_WRONG_ADDRESS =
  'Delivered to wrong address (only select this option after confirming address we have on file with member and ensuring it aligns but tracking shows delivered elsewhere)';

const NEVER_ARRIVED_OPTIONS = [
  DELIVERED_WRONG_ADDRESS,
  'Lost in transit - i.e no tracking confirmation',
  'No label created or issue with courier - i.e shipment never successfully sent',
];
const STOLEN_OPTION =
  'Package was stolen - i.e tracking shows delivered but member does not have it in their possession';
const NO_FAULT_WRONG_ADDRESS_OPTION =
  'Member issue - moved and did not notify us of new address or other location change issues';

type TriageSurveyFetchedRecord = {
  lastUpdatedDate?: string;
  createdDate?: string;
  item?: QuestionResponse[];
  triageCategory?: string;
  createTriageTask?: boolean;
  draft?: boolean;
  descriptions?: string[];
  triageRequest?: string;
};

/** Firestore returns multiple rows per orderId with undefined order; use latest by update time (matches care-hooks). */
function pickLatestTriageRecord(rows: TriageSurveyFetchedRecord[] | undefined): TriageSurveyFetchedRecord | undefined {
  if (!rows?.length) return undefined;
  return [...rows].sort((a, b) => {
    const ta = String(a.lastUpdatedDate ?? a.createdDate ?? '');
    const tb = String(b.lastUpdatedDate ?? b.createdDate ?? '');
    return tb.localeCompare(ta);
  })[0];
}

const FoodOrderTriageModalV2: React.FC<FoodOrderTriageModalV2Props> = ({
  deliveryDate,
  foodProvider,
  orderId,
  ordersData = [],
  ordersMutate,
  open,
  onClose,
  orgId,
  patientId,
  patients = {},
}) => {
  const pastOrders = useMemo(() => {
    return (ordersData || [])
      .filter((o) => o.orderId !== orderId && (!patientId || o.patientId === patientId))
      .map((o) => ({
        orderId: o.orderId || '',
        program: o.patientOrg || '—',
        memberName:
          patients && patients[o.patientId!]
            ? `${patients[o.patientId!]!.firstName || ''} ${patients[o.patientId!]!.lastName || ''}`.trim() || '—'
            : '—',
        vendor: o.vendorId || '—',
      }));
  }, [ordersData, orderId, patientId, patients]);

  const [responses, setResponses] = useState<QuestionResponse[]>([]);
  const [triageCategory, setTriageCategory] = useState<string>('');
  const [textInputs, setTextInputs] = useState<{ [key: string]: string }>({});
  const [showDraftConfirm, setShowDraftConfirm] = useState<boolean>(false);
  const [pendingAction, setPendingAction] = useState<'close' | 'draft' | null>(null);
  const [_createTriageTask, setCreateTriageTask] = useState<boolean>(true);
  const [triageRequest, setTriageRequest] = useState<string>('');
  const [isTriageSubmitted, setIsTriageSubmitted] = useState<boolean>(false);
  const [descriptions, setDescriptions] = useState<string[]>([]);
  const [linkedOrderIds, setLinkedOrderIds] = useState<string[]>([]);
  const [showLinkedOrdersConfirm, setShowLinkedOrdersConfirm] = useState(false);
  const [pendingLinkedSubmit, setPendingLinkedSubmit] = useState<{ isDraft: boolean } | null>(null);

  const { isGetLoading, postLoadingKind, getTriageSurveyResponse, postTriageSurveyResponse } =
    useFoodOrderTriageSurveyV2();

  const getAnswer = (questionId: string) => {
    const response = responses.find((r) => r.questionId === questionId);
    return response?.answerSelected[0]?.answerText;
  };

  const getAnswerId = (questionId: string) => {
    const response = responses.find((r) => r.questionId === questionId);
    return response?.answerSelected[0]?.answerId;
  };

  const handleQuestion = (questionId: string, answer: string) => {
    const answerId =
      ANSWER_IDS[answer as keyof typeof ANSWER_IDS] ??
      `PW-${answer.toUpperCase().replace(/\s+/g, '_').replace(/'/g, '')}`;

    const newResponse: QuestionResponse = {
      questionId,
      questionText: QUESTIONS[questionId as keyof typeof QUESTIONS]?.questionText ?? '',
      answerSelected: [{ answerId, answerText: answer }],
    };

    setResponses((prev) => {
      const currentIndex = TRIAGE_SURVEY_QUESTION_ORDER.indexOf(
        questionId as (typeof TRIAGE_SURVEY_QUESTION_ORDER)[number]
      );
      if (questionId === TRIAGE_QUESTION_ID.WAKE_COUNTY_GATE) {
        return [newResponse];
      }
      const filtered = prev.filter(
        (r) =>
          TRIAGE_SURVEY_QUESTION_ORDER.indexOf(r.questionId as (typeof TRIAGE_SURVEY_QUESTION_ORDER)[number]) <
          currentIndex
      );
      return [...filtered, newResponse];
    });

    // triageCategory, triageRequest, createTriageTask are computed at submit
  };

  const handleTextInput = (questionId: string, value: string) => {
    setTextInputs((prev) => ({ ...prev, [questionId]: value }));

    const upsertResponse = (qId: string, answerId: string) => {
      if (value.trim()) {
        const response: QuestionResponse = {
          questionId: qId,
          questionText: QUESTIONS[qId as keyof typeof QUESTIONS]?.questionText ?? '',
          answerSelected: [{ answerId, answerText: value }],
        };
        setResponses((prev) => {
          const idx = prev.findIndex((r) => r.questionId === qId);
          if (idx >= 0) {
            const updated = [...prev];
            updated[idx] = response;
            return updated;
          }
          return [...prev, response];
        });
      } else {
        setResponses((prev) => prev.filter((r) => r.questionId !== qId));
      }
    };

    if (questionId === TRIAGE_QUESTION_ID.DAMAGED_ITEMS_NOTE) {
      upsertResponse(TRIAGE_QUESTION_ID.DAMAGED_ITEMS_NOTE, ANSWER_IDS['Damaged items']);
    } else if (questionId === TRIAGE_QUESTION_ID.ADDITIONAL_FEEDBACK) {
      upsertResponse(TRIAGE_QUESTION_ID.ADDITIONAL_FEEDBACK, ANSWER_IDS.Feedback);
    } else if (questionId === TRIAGE_QUESTION_ID.BOX_UNATTENDED_DETAIL) {
      upsertResponse(TRIAGE_QUESTION_ID.BOX_UNATTENDED_DETAIL, ANSWER_IDS['Box unattended elaboration']);
    }
  };

  const q51 = getAnswer(TRIAGE_QUESTION_ID.WAKE_COUNTY_GATE);
  const q52 = getAnswer(TRIAGE_QUESTION_ID.WAKE_PM_SLACK_CONFIRM);
  const q03 = getAnswer(TRIAGE_QUESTION_ID.MEMBER_RECEIVED);
  const q62 = getAnswer(TRIAGE_QUESTION_ID.LINK_ANOTHER_ORDER);
  const q63 = getAnswer(TRIAGE_QUESTION_ID.PAST_ORDERS_IN_DROPDOWN);
  const q07 = getAnswer(TRIAGE_QUESTION_ID.POST_DISCHARGE_OR_EMERGENCY);
  const q16 = getAnswer(TRIAGE_QUESTION_ID.INSTACART_REPLACEMENT);
  const q19 = getAnswer(TRIAGE_QUESTION_ID.NOT_RECEIVED_REASON);
  const q34 = getAnswer(TRIAGE_QUESTION_ID.PERCENT_IMPACTED);
  const q37 = getAnswer(TRIAGE_QUESTION_ID.WILL_REPLACE_ORDER);
  const q35 = getAnswer(TRIAGE_QUESTION_ID.SEVEN_DAY_OVERRIDE);
  const q66a = getAnswer(TRIAGE_QUESTION_ID.INTERNAL_OR_VENDOR_ERROR);

  const canContinueSurvey = q51 === 'No' || (q51 === 'Yes' && q52 === 'Yes');
  const isSurveyStopped = q51 === 'Yes' && q52 === 'No';

  const isWrongAddress = q19 === DELIVERED_WRONG_ADDRESS;
  const isNourishedRxError = q66a === 'NourishedRx - internal error';
  const isVendorError = q66a === 'Vendor/Courier - external error';

  const receivedPathComplete = q03 === 'Yes' && q16;
  const notReceivedPathComplete =
    q03 === 'No' &&
    q19 &&
    (isWrongAddress
      ? q66a && q37 && (isVendorError || (isNourishedRxError && (q37 === 'No' || (q37 === 'Yes' && q35))))
      : q37);

  const readyFor62 = receivedPathComplete || notReceivedPathComplete;

  const shouldShowSubmit = () => {
    if (isSurveyStopped) return true;
    if (!canContinueSurvey) return false;
    if (!readyFor62) return false;
    if (!q62) return false;
    if (q62 === 'Yes' && !q63) return false;
    return true;
  };

  const q05b = getAnswer(TRIAGE_QUESTION_ID.RECEIVED_DAMAGE_TYPE);
  const q55a = getAnswer(TRIAGE_QUESTION_ID.IS_MF_ORDER);
  const q08 = getAnswer(TRIAGE_QUESTION_ID.BONUS_PACK);
  const q38 = getAnswer(TRIAGE_QUESTION_ID.VENDOR_REFUND);
  const q27 = getAnswer(TRIAGE_QUESTION_ID.RIGHT_LOCATION);
  const q28 = getAnswer(TRIAGE_QUESTION_ID.BOX_UNATTENDED);

  const pastOrdersRawFiltered = (pastOrders || []).filter((o) => o.orderId !== orderId);
  const pastOrdersFiltered = pastOrdersRawFiltered.map((o) => ({
    key: o.orderId,
    value: o.orderId,
    text: `${o.orderId} → ${o.program || '—'} → ${o.memberName || '—'} → ${o.vendor || '—'}`,
  }));

  const resetAllStates = () => {
    setResponses([]);
    setTriageCategory('');
    setTextInputs({});
    setDescriptions([]);
    setLinkedOrderIds([]);
  };

  useEffect(() => {
    if (!open || !orderId) {
      resetAllStates();
      return;
    }

    const fetchData = async () => {
      const getData = await getTriageSurveyResponse<TriageSurveyFetchedRecord>(orderId);
      const last = pickLatestTriageRecord(getData);
      if (last) {
        setResponses(last.item || []);
        setTriageCategory(last.triageCategory || '');
        setCreateTriageTask(last.createTriageTask ?? true);
        setIsTriageSubmitted(!last.draft);
        setDescriptions(last.descriptions || []);
        setTriageRequest(last.triageRequest || '');

        const linked = (last.item || []).find((r) => r.questionId === TRIAGE_QUESTION_ID.LINKED_ORDER_IDS);
        if (linked?.answerSelected?.length) {
          setLinkedOrderIds(
            linked.answerSelected
              .map((a) => (a.answerId ? String(a.answerId) : a.answerText != null ? String(a.answerText) : ''))
              .filter(Boolean)
          );
        } else {
          setLinkedOrderIds([]);
        }

        const newTextInputs: { [key: string]: string } = {};
        (last.item || []).forEach((r: QuestionResponse) => {
          if (r.questionId === TRIAGE_QUESTION_ID.DAMAGED_ITEMS_NOTE && r.answerSelected?.[0]?.answerText) {
            newTextInputs[TRIAGE_QUESTION_ID.DAMAGED_ITEMS_NOTE] = r.answerSelected[0].answerText;
          }
          if (r.questionId === TRIAGE_QUESTION_ID.ADDITIONAL_FEEDBACK && r.answerSelected?.[0]?.answerText) {
            newTextInputs[TRIAGE_QUESTION_ID.ADDITIONAL_FEEDBACK] = r.answerSelected[0].answerText;
          }
          if (r.questionId === TRIAGE_QUESTION_ID.BOX_UNATTENDED_DETAIL && r.answerSelected?.[0]?.answerText) {
            newTextInputs[TRIAGE_QUESTION_ID.BOX_UNATTENDED_DETAIL] = r.answerSelected[0].answerText;
          }
        });
        setTextInputs(newTextInputs);
      } else {
        resetAllStates();
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
    fetchData();
  }, [orderId, open]);

  const handleClose = () => {
    if (isTriageSubmitted) {
      resetAllStates();
      onClose();
      return;
    }
    setPendingAction('close');
    setShowDraftConfirm(true);
  };

  const handleDraft = () => {
    setPendingAction('draft');
    setShowDraftConfirm(true);
  };

  const handleSubmit = async (isDraft: boolean) => {
    let item = [...responses];
    if (linkedOrderIds.length > 0) {
      item = [
        ...item,
        {
          questionId: TRIAGE_QUESTION_ID.LINKED_ORDER_IDS,
          questionText: QUESTIONS[TRIAGE_QUESTION_ID.LINKED_ORDER_IDS].questionText,
          answerSelected: linkedOrderIds.map((id) => ({
            answerId: id,
            answerText: id,
          })),
        },
      ];
    }

    // Compute triageRequest (first match wins)
    let computedTriageRequest = '';
    if (q37 === 'Yes') computedTriageRequest = 'RESHIP';
    else if (q34 === 'More than 50% of the delivery') computedTriageRequest = 'RESHIP';
    else if (q08 === 'Yes') computedTriageRequest = 'BONUS PACK';
    else if (q38 === 'Yes') computedTriageRequest = 'REFUND ITEMS';
    else if (q16 === 'Yes') computedTriageRequest = 'RESHIP';

    const computedReship = computedTriageRequest === 'RESHIP';
    const computedInstacartOrder = q16 === 'Yes';
    const computedNoFaultToVendor = q66a === 'NourishedRx - internal error';
    const computedHighPriorityTriage = q07 === 'Yes';

    // createTriageTask: true if any trigger applies (percent answered, MF, bonus pack, replace,
    // refund, or linked orders)
    const computedCreateTriageTask =
      Boolean(q34) ||
      q55a === 'Yes' ||
      q08 === 'Yes' ||
      q37 === 'Yes' ||
      q38 === 'Yes' ||
      linkedOrderIds.length > 0;

    // Compute triageCategory: q19 mapping, then q05b, then INTERNAL_OR_VENDOR_ERROR (66a) for NRx
    // internal
    let computedTriageCategory = '';
    if (q19) {
      if (NEVER_ARRIVED_OPTIONS.includes(q19)) computedTriageCategory = 'NEVER ARRIVED';
      else if (q19 === STOLEN_OPTION) computedTriageCategory = 'STOLEN';
      else if (q19 === NO_FAULT_WRONG_ADDRESS_OPTION) computedTriageCategory = 'NO FAULT TO VENDOR - WRONG ADDRESS';
    }
    if (q05b) computedTriageCategory = 'DAMAGED OR SPOILED';
    if (q66a === 'NourishedRx - internal error') {
      computedTriageCategory = 'NO FAULT TO VENDOR - NOURISHEDRX ERROR';
    }

    // Compute descriptions from question answers
    const computedDescriptions: string[] = [];
    if (q05b) computedDescriptions.push(q05b);
    if (q19) computedDescriptions.push(q19);
    if (q66a) computedDescriptions.push(q66a);
    if (q34) computedDescriptions.push(q34);
    if (q08 === 'Yes') computedDescriptions.push('Bonus pack');
    if (q38 === 'Yes') computedDescriptions.push('Refund items');
    const q20Text = textInputs[TRIAGE_QUESTION_ID.DAMAGED_ITEMS_NOTE]?.trim();
    if (q20Text) computedDescriptions.push(q20Text);
    if (q16 === 'Yes') computedDescriptions.push('Replacing through instacart');
    if (q27 === "Yes - delivered to member's home") computedDescriptions.push('Delivered to the right location');
    if (q28 === 'Yes') computedDescriptions.push('Box sitting unattended');
    const q50Text = textInputs[TRIAGE_QUESTION_ID.ADDITIONAL_FEEDBACK]?.trim();
    if (q50Text) computedDescriptions.push(q50Text);

    const orderIds = Array.from(new Set([orderId, ...linkedOrderIds].filter((id): id is string => Boolean(id))));

    const payload = {
      triageCategory: computedTriageCategory || triageCategory,
      triageRequest: computedTriageRequest || triageRequest,
      descriptions: computedDescriptions.length > 0 ? computedDescriptions : descriptions,
      item,
      orderId,
      orderIds,
      orgId,
      patientId,
      reship: computedReship,
      instacartOrder: computedInstacartOrder,
      createTriageTask: computedCreateTriageTask,
      draft: isDraft,
      highPriorityTriage: computedHighPriorityTriage,
      assignTo: FOOD_ORDER_TRIAGE_ASSIGN_TO,
      noFaultToVendor: computedNoFaultToVendor,
    };

    await postTriageSurveyResponse(payload as Parameters<typeof postTriageSurveyResponse>[0], { submitKind: isDraft ? 'draft' : 'final' });
    await ordersMutate();
    if (!isDraft) {
      resetAllStates();
      onClose();
    }
  };

  const finishAfterDraftSave = async () => {
    await handleSubmit(true);
    resetAllStates();
    setPendingAction(null);
    onClose();
  };

  const handleDraftConfirm = () => {
    setShowDraftConfirm(false);
    if (linkedOrderIds.length > 0) {
      setPendingLinkedSubmit({ isDraft: true });
      setShowLinkedOrdersConfirm(true);
      return;
    }
    void finishAfterDraftSave();
  };

  const handleDraftCancel = () => {
    setShowDraftConfirm(false);
    setPendingAction(null);
    if (pendingAction === 'draft') return;
    resetAllStates();
    onClose();
  };

  const beginFinalSubmit = () => {
    if (linkedOrderIds.length > 0) {
      setPendingLinkedSubmit({ isDraft: false });
      setShowLinkedOrdersConfirm(true);
      return;
    }
    void handleSubmit(false);
  };

  const handleLinkedOrdersConfirmCancel = () => {
    setShowLinkedOrdersConfirm(false);
    setPendingLinkedSubmit(null);
  };

  const handleLinkedOrdersConfirmSubmit = async () => {
    if (!pendingLinkedSubmit) return;
    const { isDraft } = pendingLinkedSubmit;
    try {
      if (isDraft) {
        await finishAfterDraftSave();
      } else {
        await handleSubmit(false);
      }
    } finally {
      setShowLinkedOrdersConfirm(false);
      setPendingLinkedSubmit(null);
    }
  };

  const sharedProps = {
    handleQuestion,
    handleTextInput,
    getAnswer,
    getAnswerId,
    textInputs,
  };

  return (
    <>
      <Modal open={open} onClose={handleClose} size="large">
        <Header
          color="red"
          content="Food Order Triage Survey"
          icon={<GiHealthNormal style={{ marginTop: '-3px', marginRight: '10px' }} />}
        />
        <Modal.Content>
          {isGetLoading ? (
            <Dimmer active>
              <Loader>Loading Food Order Triage Data</Loader>
            </Dimmer>
          ) : (
            <Form>
              {foodProvider && (
                <Form.Group grouped>
                  <label>Food provider</label>
                  <p style={{ margin: '10px 0', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                    <strong>{foodProvider}</strong>
                  </p>
                </Form.Group>
              )}
              {orderId && (
                <Form.Group grouped>
                  <label>Order ID with issue</label>
                  <p style={{ margin: '10px 0', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                    <strong>{orderId}</strong>
                  </p>
                </Form.Group>
              )}
              {deliveryDate && (
                <Form.Group grouped>
                  <label>Delivery date</label>
                  <p style={{ margin: '10px 0', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                    <strong>{deliveryDate}</strong>
                  </p>
                </Form.Group>
              )}

              {canContinueSurvey && !isSurveyStopped && (
                <>
                  <WakeCountySection {...sharedProps} />
                  <MemberReceivedSection {...sharedProps} />
                  {q03 === 'Yes' && <ReceivedPathSection {...sharedProps} />}
                  {q03 === 'No' && <NotReceivedPathSection {...sharedProps} />}
                  {readyFor62 && (
                    <LinkOrderSection
                      {...sharedProps}
                      pastOrders={pastOrdersFiltered}
                      pastOrdersRaw={pastOrdersRawFiltered}
                      linkedOrderIds={linkedOrderIds}
                      onLinkedOrderIdsChange={setLinkedOrderIds}
                    />
                  )}
                </>
              )}

              {!canContinueSurvey && <WakeCountySection {...sharedProps} />}
            </Form>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleClose}>Cancel</Button>
          {!isTriageSubmitted && (
            <Button loading={postLoadingKind === 'draft'} color="orange" onClick={handleDraft}>
              Save as Draft
            </Button>
          )}
          {shouldShowSubmit() && (
            <Button loading={postLoadingKind === 'final'} primary onClick={beginFinalSubmit}>
              {isTriageSubmitted ? 'Re-submit' : 'Submit'}
            </Button>
          )}
        </Modal.Actions>
      </Modal>
      <Modal open={showDraftConfirm} size="small">
        <Header color="orange" icon="save" content="Save as Draft?" />
        <Modal.Content>
          <p>Would you like to save your progress as a draft before exiting?</p>
          {linkedOrderIds.length > 0 && (
            <p style={{ marginTop: '1em' }}>
              You have selected additional order IDs to link. After you continue, you will be asked to confirm that the
              draft will apply to those orders as well.
            </p>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleDraftCancel}>No</Button>
          <Button primary onClick={handleDraftConfirm}>
            Yes
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal
        open={showLinkedOrdersConfirm}
        size="small"
        onClose={() => {
          if (postLoadingKind !== null) return;
          handleLinkedOrdersConfirmCancel();
        }}
        closeOnDimmerClick={!postLoadingKind}
        closeOnEscape={!postLoadingKind}
      >
        <Header icon="exchange" content="Confirm linked orders" />
        <Modal.Content>
          <p>
            {pendingLinkedSubmit?.isDraft
              ? 'Saving this draft will also apply it to the selected order IDs below (in addition to the primary order).'
              : 'Submitting will update food order triage for the primary order and each selected order ID below.'}
          </p>
          <ul style={{ marginTop: '0.5em' }}>
            {orderId && (
              <li>
                <strong>{orderId}</strong> (primary)
              </li>
            )}
            {linkedOrderIds.map((id) => (
              <li key={id}>
                <strong>{id}</strong>
              </li>
            ))}
          </ul>
          <p style={{ marginTop: '1em', color: '#666' }}>
            The same triage data will be associated with each listed order ID.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleLinkedOrdersConfirmCancel} disabled={postLoadingKind !== null}>
            Cancel
          </Button>
          <Button
            primary
            loading={postLoadingKind !== null}
            disabled={postLoadingKind !== null}
            onClick={() => void handleLinkedOrdersConfirmSubmit()}
          >
            {pendingLinkedSubmit?.isDraft ? 'Save draft' : 'Submit'}
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default FoodOrderTriageModalV2;
